import { defineStore, acceptHMRUpdate } from "pinia"
import type {
  Create,
  DepartmentsRecord,
  ItemsResponse,
  ListItemsRecord,
  ListItemsResponse,
  StoresRecord,
  Update
} from "@/pocketbase-types"
import { useListStore } from "./list"
import { useOptionStore } from "./option"
import { sortBy } from "@/utils"

const requestOptions = {
  expand: "item,item.department,store"
}

export const totalWithTax = (listItems: ListItemProps[]) => {
  const cents = listItems.reduce((sum, li) => sum + li.quantity * li.price * (100 + li.taxRate), 0)
  return Math.round(cents) / 100
}

export type ItemProps = ItemsResponse<{
  department: DepartmentsRecord
}>

export type ListItemProps = ListItemsResponse<{
  item: ItemProps
  store: StoresRecord
}>

export const useListItemStore = defineStore("list-item", {
  state: () => ({
    listItems: [] as ListItemProps[]
  }),
  getters: {
    items: ({ listItems }) => listItems.map(li => li.item),
    onList: ({ listItems }) => {
      const listStore = useListStore()
      return listItems.filter(li => li.list === listStore.current?.id && !li.inCart)
    },
    inCart: ({ listItems }) => {
      const listStore = useListStore()
      return listItems.filter(li => li.list === listStore.current?.id && li.inCart)
    },
    storeSorted(): ListItemProps[] {
      return this.onList.toSorted(
        sortBy(
          li => li.expand.item.expand.department.name,
          li => li.expand.item.name
        )
      )
    },
    total(): number {
      return totalWithTax(this.onList) + this.totalInCart
    },
    totalInCart(): number {
      return totalWithTax(this.inCart)
    },
    number(): number {
      return this.onList.length + this.inCart.length
    },
    storesInCart(): string[] {
      return this.inCart.reduce((ary, li) => {
        const s = li.expand.store.name
        if (!ary.includes(s)) ary.push(s)
        return ary
      }, [] as string[])
    }
  },
  actions: {
    async refresh() {
      this.listItems = await this.pb.collection("listItems").getFullList<ListItemProps>({
        ...requestOptions,
        sort: "+item.department.name,+item.name"
      })
    },
    async create(props: Create<"listItems">) {
      const optionStore = useOptionStore()

      const { id } = await this.pb.collection("listItems").create(props)
      const newListItem = await this.pb.collection("listItems").getOne<ListItemProps>(id, requestOptions)
      this.listItems.push(newListItem)
      this.listItems.sort(
        sortBy(
          li => li.expand.item.expand.department.name,
          li => li.expand.item.name
        )
      )

      await optionStore.refresh()
    },
    async update(listItem: ListItemsRecord, props: Update<"listItems">) {
      const optionStore = useOptionStore()

      await this.pb.collection("listItems").update(listItem.id, props)
      const idx = this.listItems.findIndex(li => li.id === listItem.id)
      if (idx > -1) {
        const newListItem = await this.pb.collection("listItems").getOne<ListItemProps>(listItem.id, requestOptions)
        this.listItems.splice(idx, 1, newListItem)
      } else {
        // This should never happen
        await this.refresh()
      }

      await optionStore.refresh()
    },
    async delete(listItem: ListItemsRecord | undefined, delay = false) {
      if (!listItem) return

      await this.pb.collection("listItems").delete(listItem.id)

      const remove = () => {
        const idx = this.listItems.findIndex(li => li.id === listItem.id)
        if (idx > -1) {
          this.listItems.splice(idx, 1)
        }
      }

      if (delay) {
        setTimeout(() => remove(), 500)
      } else {
        remove()
      }
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useListItemStore, import.meta.hot))
}
